import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlService } from '@services/datasources/url.service';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { OnboardingDto } from 'app/@types/dto/onboarding-dto.model';
import { Observable } from 'rxjs';
import { AdminOrganizationListEntryDto } from 'app/@types/dto/admin-organization-list-entry-dto.model';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { PricingPlanDto } from 'app/@types/dto/pricing-plan-dto.model';
import { ResultsDetailsDto } from 'app/@types/dto/results-details-dto.model';
import { OrganizationResultFiltersDto } from 'app/@types/dto/organization-result-filters-dto.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { AdminDashboardDataDto } from 'app/@types/dto/admin-dashboard-data-dto.model';
import { CockpitHierarchyDto } from 'app/@types/dto/cockpit-hierarchy-dto.model';
import { ResultsExportTranslationDto } from 'app/@types/dto/results-export-translation-dto.model';
import { ExportType } from 'app/@types/shared/export-type-enum';

@Injectable({
    providedIn: 'root',
})
export class AdminHTTPService {
    constructor(
        private http: HttpClient,
        private urlService: UrlService,
    ) {}

    public createOrganizationUser(
        onboarding: OnboardingDto,
        clientName: string,
        dedicatedAdminId?: string,
    ): Observable<OnboardingDto> {
        return this.http.post<OnboardingDto>(
            this.urlService.createUpdateOrganisation(false, clientName, dedicatedAdminId),
            onboarding,
        );
    }

    public getOrganizationList(adminId?: string): Observable<AdminOrganizationListEntryDto[]> {
        return this.http.get<AdminOrganizationListEntryDto[]>(this.urlService.getOrganisations(adminId));
    }

    public getAdminDashboardData(
        locale: string,
        filters: OrganizationResultFiltersDto = {} as OrganizationResultFiltersDto,
    ): Observable<AdminDashboardDataDto> {
        return this.http.post<AdminDashboardDataDto>(this.urlService.getAdminDashboardData(locale), filters);
    }

    public exportAdminDashboardData(
        locale: string,
        exportType: ExportType,
        filters: OrganizationResultFiltersDto,
        translations: ResultsExportTranslationDto,
    ): Observable<Blob> {
        return this.http.post(
            this.urlService.getAdminDashboardExportUrl(locale, exportType),
            { filters, translations },
            { responseType: 'blob' },
        );
    }

    public getAllSolutionsByOrganization(adminId?: string): Observable<ResultsDetailsDto[]> {
        return this.http.get<ResultsDetailsDto[]>(this.urlService.getOrganizationsSolutions(adminId));
    }

    public getEntities(): Observable<OrganizationEntityDto[]> {
        return this.http.get<OrganizationEntityDto[]>(this.urlService.getEntities());
    }

    public getUnassignedOrganizationList(): Observable<AdminOrganizationListEntryDto[]> {
        return this.http.get<AdminOrganizationListEntryDto[]>(this.urlService.getUnassignedOrganisations());
    }

    public getOnboardingInfo(orgId: string): Observable<OnboardingDto> {
        return this.http.get<OnboardingDto>(this.urlService.getFullOnboarding(orgId));
    }

    public updateOrganization(orgId: string, org: OrganizationDto): Observable<OrganizationDto> {
        return this.http.put<OrganizationDto>(this.urlService.getFullOnboarding(orgId), org);
    }

    public deleteOrganization(orgId: string): Observable<boolean> {
        return this.http.delete<boolean>(this.urlService.getFullOnboarding(orgId));
    }

    public updateOrganizationPricingPlan(
        orgId: string,
        organizationPricingPlan: OrganizationPricingPlanDto,
    ): Observable<OrganizationPricingPlanDto> {
        return this.http.put<OrganizationPricingPlanDto>(
            this.urlService.updateOrganizationPricingPlan(orgId),
            organizationPricingPlan,
        );
    }

    public deleteDedicatedAdmin(adminId: string): Observable<boolean> {
        return this.http.delete<boolean>(this.urlService.userById(adminId));
    }

    public getDedicatedAdminList(): Observable<UserInfoDto[]> {
        return this.http.get<UserInfoDto[]>(this.urlService.getDedicatedAdmins());
    }

    public getUserById(id: string): Observable<UserInfoDto> {
        return this.http.get<UserInfoDto>(this.urlService.userById(id));
    }

    public createDedicateAdmin(user: UserInfoDto): Observable<UserInfoDto> {
        return this.http.put<UserInfoDto>(this.urlService.createDedicatedAdmin(), user);
    }

    public getOrganizationPricingPlan(orgId: string): Observable<OrganizationPricingPlanDto> {
        return this.http.get<OrganizationPricingPlanDto>(this.urlService.getOrganizationPricingPlan(orgId));
    }

    public getPricingPlan(orgId: string): Observable<PricingPlanDto> {
        return this.http.get<PricingPlanDto>(this.urlService.getPricingPlan(orgId));
    }

    public getPricingPlans(): Observable<PricingPlanDto[]> {
        return this.http.get<PricingPlanDto[]>(this.urlService.getPricingPlansUrl());
    }

    public getCockpitHierarchy(): Observable<CockpitHierarchyDto> {
        return this.http.get<CockpitHierarchyDto>(this.urlService.cockpitHierarchyUrl());
    }
}
